import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface NotificationItem {
  id: string;
  userId: string;
  title: string;
  body: string;
  category?: string;
  type?: string;
  read?: boolean;
  data?: Record<string, any>;
  sentAt?: string;
  readAt?: string;
}

export interface NotificationListResponse {
  notifications: NotificationItem[];
  total: number;
  unread: number;
  page: number;
  size: number;
}

export function useNotificationsHistory(opts: { page?: number; size?: number } = {}) {
  const { page = 0, size = 20 } = opts;
  return useQuery({
    queryKey: ["notifications", page, size],
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/micro/notifications?page=${page}&size=${size}`,
      );
      return (await res.json()) as NotificationListResponse;
    },
  });
}

/** Marque une notification comme lue — PUT /api/micro/notifications/:id/read. */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("PUT", `/api/micro/notifications/${id}/read`);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

/** Marque toutes les notifications comme lues — PUT /api/micro/notifications/read-all. */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiRequest("PUT", `/api/micro/notifications/read-all`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

/**
 * Enregistre un message en BROUILLON (mode brouillon — PAS d'envoi sortant).
 * On crée une notification ciblée sur l'utilisateur courant lui-même
 * (userId), marquée type DRAFT, avec le canal/audience visés dans `data`.
 * Aucun broadcast vers les paroissiens : seul l'auteur reçoit l'entrée.
 */
export interface MessageDraftInput {
  userId: string;
  title: string;
  body: string;
  channel?: string;
  audience?: string;
}

export function useSaveMessageDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MessageDraftInput) => {
      const res = await apiRequest("POST", "/api/micro/notifications", {
        userId: input.userId,
        title: input.title,
        body: input.body,
        category: "general", // valeur JSON de NotificationCategory (minuscules), sinon 400
        type: "DRAFT",
        data: JSON.stringify({ draft: true, channel: input.channel, audience: input.audience }),
      });
      return (await res.json()) as NotificationItem;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

/**
 * Diffusion RÉELLE d'un message à une audience (SMS/Email). Côté backend, la
 * diffusion est gardée par le flag catho.broadcast.enabled (OFF par défaut) :
 * en démo, l'appel répond 403 → on remonte un message clair, rien n'est envoyé.
 * L'appelant DOIT confirmer (aperçu du nombre de destinataires) au préalable.
 */
export interface BroadcastRecipient {
  email?: string;
  phone?: string;
}
export interface BroadcastInput {
  channel: "EMAIL" | "SMS";
  title: string;
  body: string;
  audience?: string;
  recipients: BroadcastRecipient[];
}
export interface BroadcastResult {
  channel: string;
  requested: number;
  sent: number;
  failed: number;
}

export function useBroadcastMessage() {
  return useMutation({
    mutationFn: async (input: BroadcastInput) => {
      const res = await apiRequest("POST", "/api/micro/notifications/broadcast", input);
      return (await res.json()) as BroadcastResult;
    },
  });
}

// ---------------------------------------------------------------------------
// Hub de communication de la paroisse (manuel, Action 22)
// ---------------------------------------------------------------------------

/** Ce qui partira vraiment : l'écran le dit AVANT l'envoi. */
export interface EtatDiffusion {
  notification: boolean;
  sms: boolean;
  email: boolean;
  diffusionActive: boolean;
}

export function useEtatDiffusion(enabled = true) {
  return useQuery({
    queryKey: ["notifications", "paroisse", "etat"],
    enabled,
    retry: false,
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/micro/notifications/paroisse/etat");
      return (await res.json()) as EtatDiffusion;
    },
  });
}

export interface MessageProgramme {
  id: string;
  titre: string;
  corps: string;
  scheduledAt?: string | null;
  statut: "draft" | "scheduled" | "sending" | "sent" | "cancelled";
  auteurNom?: string | null;
  envoyeLe?: string | null;
  destinataires: number;
  envoyes: number;
}

/**
 * Marge d'horloge : une notification « immédiate » porte l'heure du SERVEUR
 * au moment de l'enregistrement. Si l'horloge du poste retarde de quelques
 * secondes, cette heure paraît encore à venir à la relecture qui suit, la
 * relecture ne démarrait jamais et l'écran restait sur « elle part dans la
 * minute » (contre-relecture du 19/10/2026).
 */
const MARGE_HORLOGE_MS = 2 * 60_000;

/**
 * Un message attend-il son départ ? Programmé pour une heure passée (ou dans
 * les deux minutes, marge d'horloge) ou en cours d'envoi : notification-service
 * l'envoie à sa prochaine minute.
 */
export function enAttenteDEnvoi(liste: MessageProgramme[] | undefined, maintenant = Date.now()): boolean {
  return (liste ?? []).some(
    (p) =>
      p.statut === "sending" ||
      (p.statut === "scheduled" &&
        (!p.scheduledAt || new Date(p.scheduledAt).getTime() <= maintenant + MARGE_HORLOGE_MS)),
  );
}

export function useMessagesProgrammes(parishId: string | null | undefined) {
  return useQuery({
    queryKey: ["notifications", "paroisse", "programmes", parishId],
    enabled: !!parishId,
    retry: false,
    // Relecture toutes les 15 s tant qu'un message attend son départ : le
    // nombre de téléphones réellement atteints n'est connu qu'après l'envoi
    // (diagnostic du 19/10/2026 : l'écran annonçait « partie » à 0 téléphone).
    refetchInterval: (query) => (enAttenteDEnvoi(query.state.data as MessageProgramme[] | undefined) ? 15_000 : false),
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/micro/notifications/paroisse/programmes?parishId=${encodeURIComponent(String(parishId))}`,
      );
      const data = await res.json();
      return (Array.isArray(data) ? data : []) as MessageProgramme[];
    },
  });
}

export function useNotifierParoisse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { parishId: string; titre: string; corps: string; scheduledAt?: string }) => {
      const res = await apiRequest("POST", "/api/micro/notifications/paroisse/notification", input);
      return (await res.json()) as MessageProgramme;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", "paroisse", "programmes"] }),
  });
}

export function useAnnulerProgramme(parishId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest(
        "POST",
        `/api/micro/notifications/paroisse/programmes/${id}/annuler?parishId=${encodeURIComponent(String(parishId))}`,
      );
      return (await res.json()) as MessageProgramme;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", "paroisse", "programmes"] }),
  });
}
